/**
 * Batch #14.34 — Guide web "Immeuble" pour le portail client.
 *
 * User a demandé : remplacer le PDF par une vraie page web style index/wiki
 * avec sections, captures d'écran et explications détaillées.
 *
 * Structure :
 * - Hero avec table des matières flottante (desktop)
 * - 8 sections thématiques (logements, baux, paiements, maintenance, etc.)
 * - Boîtes "Astuce" / "Important" / "Étape par étape"
 * - Illustrations via lucide-react + boxes captures (placeholder visuel
 *   en attendant les vrais screenshots à uploader dans /public/guides/immeuble/)
 *
 * NB : aucun téléchargement PDF — page consultable en ligne uniquement.
 */
import { useState, useEffect, useRef } from "react";
import {
  ArrowLeft, Home, Building2, Users, FileSignature, Wrench, CreditCard,
  Camera, Upload, Shield, MessageCircle, BookOpen, CheckCircle2,
  AlertTriangle, Lightbulb, Smartphone, ChevronRight,
} from "lucide-react";
import { ILLUSTRATIONS } from "@/pages/portal/guides/illustrations/ImmeubleIllustrations";

// ------------------------------------------------------------------
// Sections du guide — déclaratives pour pouvoir générer la TOC auto
// ------------------------------------------------------------------
const SECTIONS = [
  { id: "introduction",  label: "Introduction",          icon: BookOpen },
  { id: "logements",     label: "Mes logements",          icon: Building2 },
  { id: "locataires",    label: "Locataires & baux",      icon: Users },
  { id: "paiements",     label: "Paiements de loyer",     icon: CreditCard },
  { id: "documents",     label: "Coffre-fort & documents", icon: Upload },
  { id: "maintenance",   label: "Maintenance & tickets",  icon: Wrench },
  { id: "enquete",       label: "Enquête de crédit",      icon: Shield },
  { id: "communication", label: "Communiquer avec l'équipe", icon: MessageCircle },
];

// ------------------------------------------------------------------
// Composants réutilisables
// ------------------------------------------------------------------
function Section({ id, title, icon: Icon, children, accent = "cyan" }) {
  return (
    <section id={id} className="scroll-mt-20" data-testid={`immeuble-section-${id}`}>
      <div className="flex items-center gap-3 mb-4 pb-3 border-b border-slate-200">
        <div className={`w-11 h-11 rounded-xl bg-${accent}-100 text-${accent}-700 flex items-center justify-center shrink-0`}>
          <Icon className="w-5 h-5" />
        </div>
        <h2 className="text-xl sm:text-2xl font-bold text-slate-900">{title}</h2>
      </div>
      <div className="space-y-4 text-[15px] text-slate-700 leading-relaxed">
        {children}
      </div>
    </section>
  );
}

function Tip({ children, icon: Icon = Lightbulb, tone = "amber" }) {
  const tones = {
    amber:   { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-900", iconBg: "bg-amber-100 text-amber-700", label: "Astuce" },
    emerald: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-900", iconBg: "bg-emerald-100 text-emerald-700", label: "Bon à savoir" },
    rose:    { bg: "bg-rose-50", border: "border-rose-200", text: "text-rose-900", iconBg: "bg-rose-100 text-rose-700", label: "Important" },
  };
  const t = tones[tone] || tones.amber;
  return (
    <div className={`${t.bg} ${t.border} border rounded-xl p-4 flex items-start gap-3`}>
      <div className={`w-8 h-8 rounded-lg ${t.iconBg} flex items-center justify-center shrink-0`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className={`text-sm ${t.text} leading-relaxed`}>
        <div className="font-semibold mb-0.5">{t.label}</div>
        {children}
      </div>
    </div>
  );
}

function Step({ n, title, children }) {
  return (
    <div className="flex gap-4">
      <div className="w-9 h-9 rounded-full bg-cyan-600 text-white font-bold flex items-center justify-center shrink-0 text-sm shadow-sm">
        {n}
      </div>
      <div className="flex-1 pt-1">
        <div className="font-semibold text-slate-800 mb-1">{title}</div>
        <div className="text-sm text-slate-600 leading-relaxed">{children}</div>
      </div>
    </div>
  );
}

/**
 * Boîte "Capture d'écran" — placeholder élégant en attendant les vraies
 * images. Quand vous aurez les screenshots, déposez-les dans
 *   /app/frontend/public/guides/immeuble/{slug}.png
 * et remplacez le contenu par : <img src={`/guides/immeuble/${slug}.png`} ...
 */
function Screenshot({ alt, slug, height = 240, caption }) {
  const [hasImage, setHasImage] = useState(false);
  useEffect(() => {
    const img = new Image();
    img.onload = () => setHasImage(true);
    img.onerror = () => setHasImage(false);
    img.src = `${process.env.PUBLIC_URL || ""}/guides/immeuble/${slug}.png`;
  }, [slug]);

  // Batch #14.34 — Illustration SVG inline si disponible pour ce slug,
  // sinon image PNG, sinon placeholder.
  const Illustration = ILLUSTRATIONS[slug];

  return (
    <figure className="my-3" data-testid={`immeuble-screenshot-${slug}`}>
      <div
        className="rounded-xl border-2 border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100 overflow-hidden shadow-sm"
      >
        {hasImage ? (
          <img
            src={`${process.env.PUBLIC_URL || ""}/guides/immeuble/${slug}.png`}
            alt={alt}
            className="w-full h-auto"
          />
        ) : Illustration ? (
          <Illustration />
        ) : (
          <div className="h-full flex flex-col items-center justify-center text-slate-400 p-8" style={{ minHeight: `${height}px` }}>
            <Camera className="w-10 h-10 mb-2 opacity-50" />
            <div className="text-xs uppercase tracking-wider font-semibold mb-1">Capture d'écran</div>
            <div className="text-sm text-center max-w-md italic">{alt}</div>
          </div>
        )}
      </div>
      {caption && (
        <figcaption className="text-xs text-slate-500 mt-2 text-center italic">{caption}</figcaption>
      )}
    </figure>
  );
}

// ------------------------------------------------------------------
// Composant principal
// ------------------------------------------------------------------
export default function ImmeubleGuide({ onBack, primaryColor = "#0E7490" }) {
  const [activeId, setActiveId] = useState("introduction");
  const contentRef = useRef(null);

  // Highlight de la section active selon le scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActiveId(e.target.id);
        });
      },
      { rootMargin: "-30% 0px -60% 0px" },
    );
    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="max-w-6xl mx-auto" data-testid="immeuble-guide-page">
      {/* Top bar : retour + titre */}
      <div className="flex items-center gap-3 mb-5">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 transition"
          data-testid="immeuble-guide-back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Retour aux guides
        </button>
      </div>

      {/* Hero */}
      <div className="bg-gradient-to-br from-cyan-600 via-cyan-700 to-teal-700 rounded-2xl shadow-lg overflow-hidden mb-6">
        <div className="px-6 py-8 sm:px-10 sm:py-12 text-white">
          <div className="flex items-start gap-4 mb-3">
            <div className="w-14 h-14 rounded-2xl bg-white/15 backdrop-blur-sm flex items-center justify-center shrink-0">
              <Home className="w-7 h-7" />
            </div>
            <div>
              <div className="text-cyan-100 text-xs uppercase tracking-wider font-semibold">Mode d'emploi</div>
              <h1 className="text-2xl sm:text-3xl font-bold leading-tight">Module Immeuble</h1>
            </div>
          </div>
          <p className="text-cyan-50 text-[15px] leading-relaxed max-w-2xl">
            Tout ce que vous devez savoir pour gérer vos immeubles, locataires, baux,
            paiements de loyer et entretien depuis votre portail.
          </p>
        </div>
      </div>

      {/* Layout 2 colonnes : TOC sticky + contenu */}
      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-8">
        {/* TOC sticky */}
        <aside className="hidden lg:block">
          <nav className="sticky top-20 space-y-1" data-testid="immeuble-guide-toc">
            <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-2 px-3">
              Table des matières
            </div>
            {SECTIONS.map((s) => {
              const SIcon = s.icon;
              const isActive = activeId === s.id;
              return (
                <button
                  key={s.id}
                  onClick={() => scrollTo(s.id)}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm transition flex items-center gap-2 ${
                    isActive
                      ? "bg-cyan-50 text-cyan-700 font-semibold border-l-2 border-cyan-600"
                      : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  }`}
                  data-testid={`immeuble-guide-toc-${s.id}`}
                >
                  <SIcon className={`w-4 h-4 shrink-0 ${isActive ? "text-cyan-600" : "text-slate-400"}`} />
                  <span className="flex-1 truncate">{s.label}</span>
                  {isActive && <ChevronRight className="w-3 h-3 text-cyan-600" />}
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Contenu principal */}
        <article ref={contentRef} className="space-y-10 pb-10">
          {/* TOC mobile */}
          <div className="lg:hidden bg-slate-50 rounded-xl p-4 border border-slate-200" data-testid="immeuble-guide-toc-mobile">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">
              Table des matières
            </div>
            <div className="flex flex-wrap gap-1.5">
              {SECTIONS.map((s) => (
                <button
                  key={s.id}
                  onClick={() => scrollTo(s.id)}
                  className="px-2.5 py-1 rounded-full bg-white border border-slate-200 text-[11px] text-slate-700 hover:border-cyan-400 hover:text-cyan-700"
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* ─── INTRODUCTION ─── */}
          <Section id="introduction" title="Introduction" icon={BookOpen} accent="cyan">
            <p>
              Le module <strong>Immeuble</strong> est votre centre de gestion locative. Il vous permet
              de centraliser, en un seul endroit, toute l'information de vos immeubles à revenus :
              logements, baux, locataires, paiements de loyer, entretien, dépenses et documents fiscaux.
            </p>
            <p>
              Tout ce que vous saisissez est partagé en temps réel avec votre équipe
              (DG Comptabilité), ce qui simplifie énormément vos déclarations fiscales (TP-128,
              T776, etc.) et la production de vos états financiers annuels.
            </p>
            <Tip tone="emerald" icon={CheckCircle2}>
              Vous pouvez accéder à votre portail depuis votre téléphone, tablette ou ordinateur.
              Pour une expérience optimale, installez-le comme application
              (cf. courriel d'invitation reçu).
            </Tip>
          </Section>

          {/* ─── LOGEMENTS ─── */}
          <Section id="logements" title="Mes logements" icon={Building2} accent="cyan">
            <p>
              C'est ici que vous déclarez chacun de vos immeubles et leurs appartements (logements,
              chambres, locaux commerciaux). Cette section sert de base à tout le reste : sans logement,
              pas de bail, pas de loyer, pas de dépense.
            </p>
            <Screenshot
              slug="logements-liste"
              alt="Page Gestion immeuble — vue détaillée d'un appartement avec actions rapides, locataires et journal technique"
              caption="Page de gestion d'un appartement (Immeuble Démo A · Appartement 1A)"
            />
            <div className="space-y-3 mt-4">
              <Step n={1} title="Naviguer dans vos immeubles">
                Utilisez les <strong>menus déroulants « Immeuble » et « Appartement »</strong> en bas
                à gauche pour passer d'une unité à l'autre. Le <strong>fil d'Ariane</strong> en haut
                (Mes immeubles → Immeuble Démo A → Appartement 1A) vous indique votre position exacte.
              </Step>
              <Step n={2} title="Actions rapides">
                Les boutons verts et bleus <strong>« + Gérer un immeuble »</strong> et
                <strong> « + Gérer un appartement »</strong> permettent d'ajouter ou modifier
                rapidement une unité sans changer de page.
              </Step>
              <Step n={3} title="Tableau de bord par appartement">
                Chaque appartement affiche <strong>3 cartes essentielles</strong> :
                la fiche identité (locataires actifs, statut du bail),
                les <strong>locataires</strong> avec l'état des paiements par mois (J F M A M J J A S O N D),
                et le <strong>journal technique</strong> (à faire / en cours / terminé) pour suivre
                les réparations et anomalies.
              </Step>
            </div>
            <Tip>
              Vous pouvez attacher des <strong>photos et tickets de maintenance</strong> à chaque
              appartement. Le journal technique passe automatiquement en rouge si un ticket
              « À faire » dépasse un certain délai — utile pour ne rien oublier.
            </Tip>
          </Section>

          {/* ─── LOCATAIRES & BAUX ─── */}
          <Section id="locataires" title="Locataires & baux" icon={Users} accent="indigo">
            <p>
              Pour chaque logement loué, créez un <strong>bail TAL conforme à la loi québécoise</strong>
              qui lie un ou plusieurs locataires à une unité. Le bail est signé électroniquement
              par toutes les parties via un workflow multi-signataires sécurisé.
            </p>
            <Screenshot
              slug="bail-creation"
              alt="Workflow Bail TAL — liste de brouillons à gauche, signataires (locataires + propriétaires), initiales auto, onglets Page 1/2/3"
              caption="Workflow « Bail TAL — Nouveau workflow » avec multi-signataires"
            />
            <div className="space-y-3 mt-4">
              <Step n={1} title="Créer un nouveau bail">
                Cliquez sur <strong>« + Nouveau bail »</strong> en haut à droite. Un brouillon
                vide apparaît dans la liste de gauche, prêt à être rempli.
              </Step>
              <Step n={2} title="Ajouter les signataires (jusqu'à 4)">
                Dans la carte <strong>« Signataires »</strong>, ajoutez <strong>1 ou 2 locataires</strong>
                (nom + courriel) et <strong>1 ou 2 propriétaires</strong>. Le système supporte
                automatiquement les co-baux (couples, colocataires) et la co-propriété.
                Les compteurs <em>(0/2)</em> indiquent qui reste à ajouter.
              </Step>
              <Step n={3} title="Remplir le formulaire TAL (3 pages)">
                Les <strong>onglets « Page 1 » / « Page 2 » / « Page 3 »</strong> regroupent
                les sections A à H du formulaire officiel TAL : parties, logement, durée,
                loyer, services, conditions, accessoires. Tous les champs sont validés
                en direct.
              </Step>
              <Step n={4} title="Initiales internes automatiques">
                Le système place <strong>automatiquement les initiales</strong> de chaque
                signataire au pied de chaque page, ainsi qu'aux endroits clés du formulaire
                (Section B détecteur de fumée, Section D loyer, Section E règlement immeuble) —
                conforme aux exigences TAL.
              </Step>
              <Step n={5} title="Envoyer pour signature séquentielle">
                Cliquez sur <strong>« Envoyer au 1er signataire »</strong>. Chaque locataire et
                propriétaire reçoit son lien magique par courriel, signe, puis le suivant est
                automatiquement notifié. Vous voyez l'avancement en temps réel dans la liste
                de gauche.
              </Step>
              <Step n={6} title="Bail signé et archivé">
                Une fois toutes les signatures collectées, le PDF final (avec QR code de
                vérification publique) est archivé automatiquement en sécurité
                et envoyé par courriel à toutes les parties + à l&apos;équipe.
              </Step>
            </div>
            <Tip tone="rose" icon={AlertTriangle}>
              <strong>Renouvellement automatique</strong> : à la date de fin du bail, le système
              vous propose 30 jours à l'avance de créer un <strong>avis de renouvellement</strong>
              (avec ou sans hausse) à envoyer aux 2 locataires en parallèle. Vous restez maître
              de la décision finale.
            </Tip>
          </Section>

          {/* ─── PAIEMENTS DE LOYER ─── */}
          <Section id="paiements" title="Paiements de loyer" icon={CreditCard} accent="emerald">
            <p>
              Vous avez <strong>4 façons</strong> de recevoir les loyers — et toutes sont trackées
              automatiquement dans votre portail :
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
              <div className="border border-emerald-200 bg-emerald-50/50 rounded-xl p-4">
                <div className="font-semibold text-emerald-900 mb-1">💳 Carte (Stripe)</div>
                <p className="text-xs text-emerald-800">
                  Le locataire reçoit un lien de paiement. Le montant <em>net (moins les frais)</em>
                  est déposé sur votre compte bancaire 2-3 jours plus tard. 100 % automatique.
                </p>
              </div>
              <div className="border border-violet-200 bg-violet-50/50 rounded-xl p-4">
                <div className="font-semibold text-violet-900 mb-1">🔁 Prélèvement automatique (PAD)</div>
                <p className="text-xs text-violet-800">
                  Le locataire autorise un prélèvement mensuel récurrent sur son compte. Vous
                  recevez le loyer <em>net (moins les frais)</em> automatiquement chaque mois,
                  sans rappel. Idéal pour les baux long terme.
                </p>
              </div>
              <div className="border border-blue-200 bg-blue-50/50 rounded-xl p-4">
                <div className="font-semibold text-blue-900 mb-1">🏦 Virement Interac</div>
                <p className="text-xs text-blue-800">
                  Le locataire envoie un virement à votre adresse courriel. Vous le marquez
                  manuellement comme reçu dans le portail.
                </p>
              </div>
              <div className="border border-amber-200 bg-amber-50/50 rounded-xl p-4">
                <div className="font-semibold text-amber-900 mb-1">📝 Chèque ou comptant</div>
                <p className="text-xs text-amber-800">
                  Vous saisissez le paiement manuellement avec la date et le montant.
                  Le système génère un reçu pour le locataire.
                </p>
              </div>
            </div>
            <Screenshot
              slug="paiements-vue"
              alt="Formulaire « Prélèvements Stripe (PAD) » — sélecteur Appartement/Locataire, infos bancaires (institution/transit/compte), nom au compte, type de compte (chèque/épargne), date du mandat, case consentement obligatoire"
              caption="Configuration d'un Prélèvement Automatique Bancaire (PAD) pour un locataire"
              height={420}
            />
            <div className="space-y-3 mt-4">
              <Step n={1} title="Choisir le locataire">
                Sélectionnez <strong>l'appartement</strong> dans le menu en haut. Le système
                pré-affiche le <strong>locataire actif</strong> de cet appartement (ex.
                « Apt 1A — Alice Locataire »).
              </Step>
              <Step n={2} title="Renseigner les coordonnées bancaires">
                Saisissez les <strong>3 numéros</strong> du chèque du locataire :
                <em> Institution</em> (3 chiffres),
                <em> Transit</em> (5 chiffres),
                <em> N° de compte</em> (7 à 12 chiffres).
                Ces données sont chiffrées et stockées en sécurité par Stripe (PCI-DSS).
              </Step>
              <Step n={3} title="Nom, type de compte et date du mandat">
                Indiquez le <strong>nom complet</strong> du titulaire du compte
                (souvent identique au nom du locataire), le <strong>type de compte</strong>
                (chèque ou épargne), et la <strong>date</strong> à laquelle le locataire
                a signé le mandat papier.
              </Step>
              <Step n={4} title="Confirmer le mandat signé">
                Cochez la case <strong>« Je confirme que le mandat PAD a été dûment signé
                par le locataire »</strong> puis cliquez sur <strong>« Activer le prélèvement »</strong>.
                À partir du 1<sup>er</sup> du mois suivant, le loyer sera prélevé automatiquement.
              </Step>
            </div>
            <Tip tone="emerald" icon={CheckCircle2}>
              <strong>Mandat PAD obligatoire</strong> : Stripe / Paiements Canada exigent un
              <strong> mandat papier signé</strong> par le locataire avant le 1<sup>er</sup>
              prélèvement (Règle H1 Paiements Canada). Téléchargez le modèle PDF préconfiguré
              dans <em>Documents → Mandats PAD</em>, faites-le signer et conservez-le 7 ans.
            </Tip>
            <Tip tone="rose" icon={AlertTriangle}>
              <strong>Frais de transaction Stripe</strong> : ~1 % du montant prélevé (PAD) ou
              2,9 % + 0,30 $ (carte). Détail exact des coûts applicables à votre situation —
              contactez <strong>DG Comptabilité</strong> pour la grille à jour.
            </Tip>
          </Section>

          {/* ─── COFFRE-FORT & DOCUMENTS ─── */}
          <Section id="documents" title="Coffre-fort & documents" icon={Upload} accent="violet">
            <p>
              Le coffre-fort est l'endroit où vous classez tous les documents légaux et fiscaux
              de vos immeubles : taxes municipales, taxes scolaires, assurances, contrats
              d'entretien, certificats de localisation, baux signés, factures et correspondance.
            </p>
            <p>
              <strong>Tout ce que vous déposez ici</strong> est automatiquement partagé avec votre
              équipe (DG Comptabilité) pour qu'elle l'utilise lors de vos déclarations
              fiscales annuelles (T776, TP-128).
            </p>
            <Screenshot
              slug="coffre-fort"
              alt="Formulaire unifié Documents & Coffre-fort — sélecteurs Immeuble/Appartement + 9 catégories visuelles (taxes municipales/scolaires, assurances, évaluation, factures, baux, correspondance, autre)"
              caption="Formulaire unifié de dépôt avec 9 catégories visuelles"
            />
            <div className="space-y-3 mt-4">
              <Step n={1} title="Choisir l'immeuble + l'appartement (optionnel)">
                En haut, sélectionnez <strong>l'immeuble</strong> dans le menu déroulant.
                Si le document concerne un appartement spécifique (ex. bail), choisissez aussi
                <strong> l'appartement</strong>. Sinon laissez sur <em>« Global immeuble »</em>
                pour les taxes, assurances, etc.
              </Step>
              <Step n={2} title="Sélectionner la catégorie">
                Cliquez sur l'une des <strong>9 tuiles visuelles</strong> :
                Taxes municipales · Taxes scolaires · Assurance bâtiment ·
                Assurance responsabilité · Évaluation municipale · Facture / Reçu ·
                Bail / Contrat · Correspondance / Avis · Autre document.
                La tuile choisie passe en couleur pour confirmer.
              </Step>
              <Step n={3} title="Indiquer l'année et le montant">
                Renseignez <strong>l'année fiscale</strong> concernée et, si applicable,
                le <strong>montant</strong> (utile pour les taxes et assurances qui affichent
                le total annuel sur votre relevé fiscal).
              </Step>
              <Step n={4} title="Joindre le fichier">
                Glissez-déposez le PDF, ou prenez-le directement <strong>en photo</strong> depuis
                votre mobile (bouton appareil photo). Formats acceptés : PDF, JPG, PNG, HEIC.
              </Step>
            </div>
            <Tip>
              💡 <strong>Astuce mobile</strong> : si vous recevez votre compte de taxes par la
              poste, ouvrez le portail sur votre téléphone et photographiez-le directement
              depuis le formulaire. L'équipe le reçoit en temps réel, sans passer par
              le courriel.
            </Tip>
          </Section>

          {/* ─── MAINTENANCE ─── */}
          <Section id="maintenance" title="Maintenance & tickets techniques" icon={Wrench} accent="amber">
            <p>
              Le <strong>Journal technique</strong> de chaque appartement vous permet de tracker
              toutes les réparations, entretiens et anomalies signalés par vos locataires —
              avec catégorie (Plomberie, Électricité, Chauffage…), priorité et statut.
            </p>
            <Screenshot
              slug="maintenance-ticket"
              alt="Carte « Journal technique » d'un appartement — bouton + Ticket, compteurs (1 À FAIRE / 0 EN COURS / 0 TERMINÉ), ticket exemple « Robinet cuisine qui fuit » avec badges À faire · normal · Plomberie"
              caption="Journal technique d'un appartement — un ticket en cours"
              height={320}
            />
            <div className="space-y-3 mt-4">
              <Step n={1} title="Créer un ticket">
                Cliquez sur le bouton orange <strong>« + Ticket »</strong> en haut à droite
                du Journal technique. Renseignez un <strong>titre court</strong> (ex.
                « Robinet cuisine qui fuit »), une <strong>catégorie</strong> (Plomberie,
                Électricité, Chauffage, Serrurerie, Autre), une <strong>priorité</strong>
                (basse / normale / haute) et une description complète.
              </Step>
              <Step n={2} title="Suivre l'avancement (3 statuts)">
                Chaque ticket évolue dans <strong>3 colonnes</strong> :
                🟠 <em>À faire</em> (en attente d'intervention),
                🔵 <em>En cours</em> (artisan contacté ou intervention démarrée),
                🟢 <em>Terminé</em> (réglé + facture archivée). Les compteurs en haut
                donnent un aperçu rapide.
              </Step>
              <Step n={3} title="Modifier / fermer le ticket">
                Le petit crayon ✏️ à droite du ticket permet de modifier les détails ou
                changer le statut. Le « ✕ » rouge ferme définitivement le ticket
                (archivé mais conservé pour l'historique).
              </Step>
            </div>
            <Tip tone="emerald" icon={CheckCircle2}>
              <strong>Avantage fiscal</strong> : les <strong>réparations</strong>
              (robinet, peinture après dégât d'eau, etc.) sont déductibles à 100 %
              dans l'année. Les <strong>améliorations</strong> (changement complet de
              cuisine, nouvelles fenêtres) sont amortissables. L'équipe fait la
              classification — votre rôle est juste de bien documenter (photos avant/après,
              factures liées au ticket).
            </Tip>
          </Section>

          {/* ─── ENQUÊTE DE CRÉDIT ─── */}
          <Section id="enquete" title="Enquête de crédit (locataire)" icon={Shield} accent="blue">
            <p>
              Avant de signer un bail, vous pouvez demander une <strong>enquête de crédit</strong>
              officielle sur le candidat locataire. Le rapport est produit par
              <strong> le cabinet</strong> (avec données <em>Équifax</em>) et facturé
              au tarif unique de <strong>39,98 $</strong> par enquête.
            </p>
            <Screenshot
              slug="enquete-credit"
              alt="Formulaire « Enquête de crédit · Vérification candidat locataire » — tarif 39,98 $, identité + coordonnées + adresse actuelle + case consentement"
              caption="Formulaire d'enquête de crédit (39,98 $)"
            />
            <div className="space-y-3 mt-4">
              <Step n={1} title="Section « Identité du candidat »">
                Saisissez le <strong>prénom</strong>, le <strong>nom</strong> et la
                <strong> date de naissance</strong> exacte. Ces 3 champs sont obligatoires pour
                que le dossier crédit du candidat puisse être retrouvé.
              </Step>
              <Step n={2} title="Section « Coordonnées »">
                <strong>Téléphone</strong> et <strong>courriel</strong> du candidat. Le candidat
                recevra une notification automatique quand le rapport sera prêt s'il souhaite
                en recevoir une copie.
              </Step>
              <Step n={3} title="Section « Adresse actuelle »">
                Adresse complète actuelle du candidat (rue, ville, code postal). Cette donnée
                est croisée avec les dossiers Équifax / TransUnion pour valider l'identité.
              </Step>
              <Step n={4} title="Confirmer le consentement et soumettre">
                Cochez la case <strong>« Je confirme avoir obtenu le consentement écrit
                du candidat »</strong>, puis cliquez sur <strong>« Lancer l'enquête (39,98 $) »</strong>.
                Vous serez redirigé vers Stripe pour le paiement sécurisé.
              </Step>
            </div>
            <Tip tone="rose" icon={AlertTriangle}>
              <strong>Obligation légale</strong> : le consentement écrit (papier ou électronique)
              du candidat est <em>obligatoire</em> avant toute enquête. Conservez-le précieusement
              (la case à cocher du formulaire vaut engagement juridique de votre part).
            </Tip>
            <Tip>
              ⏱️ <strong>Délai</strong> : le rapport de locataire est préparé par le cabinet dès la réception
              de votre demande (généralement <strong>le jour même</strong>). Il vous est livré par courriel + dans le
              portail (tuile Enquête de crédit → <em>Demandes récentes</em> → bouton <em>Rapport PDF</em>, et dans le coffre-fort de l'immeuble).
              Le coût de 39,98 $ est facturé une seule fois par enquête, peu importe l'issue (accepté/refusé).
            </Tip>
          </Section>

          {/* ─── COMMUNICATION ─── */}
          <Section id="communication" title="Communiquer avec l'équipe" icon={MessageCircle} accent="indigo">
            <p>
              Si vous avez une question sur un dossier, une déclaration fiscale ou une opération,
              vous avez 3 canaux :
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
              <div className="border border-slate-200 rounded-xl p-4 bg-white">
                <Smartphone className="w-5 h-5 text-indigo-600 mb-2" />
                <div className="font-semibold text-sm text-slate-800">Chat portail</div>
                <p className="text-xs text-slate-600 mt-1">
                  Tuile « Chat » dans le portail. Notre équipe répond en moyenne en
                  3 h ouvrables.
                </p>
              </div>
              <div className="border border-slate-200 rounded-xl p-4 bg-white">
                <MessageCircle className="w-5 h-5 text-indigo-600 mb-2" />
                <div className="font-semibold text-sm text-slate-800">Courriel</div>
                <p className="text-xs text-slate-600 mt-1">
                  dgfiscalflow@dgcomptabilite.ca — pour les questions plus longues.
                </p>
              </div>
              <div className="border border-slate-200 rounded-xl p-4 bg-white">
                <FileSignature className="w-5 h-5 text-indigo-600 mb-2" />
                <div className="font-semibold text-sm text-slate-800">Rendez-vous</div>
                <p className="text-xs text-slate-600 mt-1">
                  Tuile « Rendez-vous » → choisissez un créneau dans notre calendrier en ligne.
                </p>
              </div>
            </div>
            <Tip tone="emerald" icon={CheckCircle2}>
              Toutes les communications restent dans votre portail — vous gardez un historique
              complet de chaque échange avec votre équipe.
            </Tip>
          </Section>

          {/* Footer du guide */}
          <div className="mt-10 pt-6 border-t border-slate-200 text-center">
            <p className="text-sm text-slate-500">
              📘 Ce guide est mis à jour régulièrement. Une question, une suggestion d'amélioration ?
              Contactez-nous via la tuile <strong>« Chat »</strong> de votre portail.
            </p>
            <button
              onClick={onBack}
              className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-cyan-600 text-white text-sm font-medium hover:bg-cyan-700"
              data-testid="immeuble-guide-footer-back-btn"
            >
              <ArrowLeft className="w-4 h-4" /> Retour aux autres guides
            </button>
          </div>
        </article>
      </div>
    </div>
  );
}
